/**
 * Shopinfo — FONTE de rates do PRODUTO VISTO via Storefront API (TASK-052/053)
 * O endpoint AJAX cota o CARRINHO; a PDP precisa do produto olhado, com carrinho
 * vazio ou divergente — daí o cart efêmero de 1 linha. @defer é OBRIGATÓRIO para
 * carrier rates: resposta multipart/mixed, rates no chunk incremental. cart.id
 * traz o `?key=`, que dá escrita no cart: só sessionStorage, nunca log.
 * O PRAZO (deliveryPromise) só existe na API `unstable`, então a query de rates
 * mira lá e cai na versão estável se algo falhar — ver fetchRates.
 *
 * Arquivo PURO (TASK-053): rede, cache de sessão e parser. Zero DOM, zero
 * locale, zero conhecimento do calculador global. Quem exibe é o
 * shopinfo-shipping-inline.js, que depende DESTE — a dependência aponta numa
 * direção só, e nada aqui sabe que o inline ou o modal existem.
 */
(function () {
  const TTL_MS = 15 * 60 * 1000;
  const MAX_ENTRIES = 20;
  const CACHE_KEY = 'shopinfo_rates_cache';
  const CART_KEY = 'shopinfo_quote_cart';
  // `_` = attribute oculto: separa o cart de cotação do carrinho real.
  const CART_ATTR = '_shopinfo_cotacao';

  const FIELDS = 'cart { id lines(first: 1) { nodes { id } } } userErrors { field message }';
  // `field` é o caminho do input que a mutation recusou. Só o que aponta para o
  // endereço de entrega vira veredito sobre o CEP; ver cartUserError.
  const ADDRESS_FIELD_RE = /deliver|address/i;
  const MUT_CREATE = `mutation q($input: CartInput!) { cartCreate(input: $input) { ${FIELDS} } }`;
  const MUT_LINES = `mutation q($id: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $id, lines: $lines) { ${FIELDS} } }`;
  // `deliveryPromise` é o prazo que o carrier declara ("3 a 5 dias úteis") — o
  // MESMO texto que o checkout mostra. Ele só existe na versão `unstable` da
  // Storefront API: verificado ao vivo em 2026-08-21, 2026-07 e 2026-10 não têm
  // o campo. Daí a query nascer em duas formas — com o campo, contra a
  // unstable; sem ele, contra a versão estável do data-attribute.
  const query = (promise) =>
    `query q($id: ID!) { cart(id: $id) { id ...Rates @defer } }
    fragment Rates on Cart { deliveryGroups(first: 10, withCarrierRates: true) {
      nodes { deliveryOptions { title description handle${promise ? ' deliveryPromise' : ''}
        estimatedCost { amount } } } } }`;

  // `/api/2026-07/graphql.json` → `/api/unstable/graphql.json`. Só a QUERY de
  // rates viaja para a unstable; o cartCreate NUNCA sai da estável. Misturar as
  // versões sobre o mesmo cart id foi verificado ao vivo e funciona.
  const VERSION_RE = /\/api\/[^/]+\/graphql\.json/;

  // Faixa ("2 a 5 dias") antes do valor único ("até 5 dias úteis").
  const RANGE_RE = /(\d+)\s*(?:a|e|-|até|to)\s*(\d+)\s*dias?/i;
  const SINGLE_RE = /(\d+)\s*dias?/i;

  const inflight = new Map();

  // `kind` é o contrato de erro compartilhado com o calculador: 'coverage'
  // acusa o endereço, 'network' acusa a loja. Nunca trocar um pelo outro.
  function kindError(msg, kind) {
    const err = new Error(msg);
    err.shopinfoKind = kind;
    return err;
  }

  function netError(msg) {
    return kindError(msg, 'network'); // falha de infra nunca acusa o CEP (TASK-051)
  }

  function ssGet(k) {
    try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch (e) { return null; }
  }
  function ssSet(k, v) {
    try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {}
  }

  function cacheGet(key) {
    const hit = (ssGet(CACHE_KEY) || {})[key];
    return hit && Date.now() - hit.t <= TTL_MS ? hit.r : null;
  }

  function cacheSet(key, rates) {
    const all = ssGet(CACHE_KEY) || {};
    all[key] = { t: Date.now(), r: rates };
    const keys = Object.keys(all);
    // Teto duro: as entradas mais velhas saem, a storage não cresce sozinha.
    if (keys.length > MAX_ENTRIES) {
      keys.sort((a, b) => all[a].t - all[b].t);
      keys.slice(0, keys.length - MAX_ENTRIES).forEach((k) => delete all[k]);
    }
    ssSet(CACHE_KEY, all);
  }

  function gid(id) {
    const v = String(id || '');
    return v.indexOf('gid://') === 0 ? v : 'gid://shopify/ProductVariant/' + v.replace(/\D/g, '');
  }

  async function gql(cfg, query, variables) {
    const res = await fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
      body: JSON.stringify({ query: query, variables: variables }),
      signal: cfg.signal,
    });
    if (!res.ok) throw netError('HTTP ' + res.status);
    return res;
  }

  // Boundary SEMPRE do header, nunca hardcoded. Recortar do primeiro `{` ao
  // último `}` dispensa parsear cabeçalho de parte e sobrevive a \r\n vs \n.
  function jsonChunks(text, contentType) {
    const m = /boundary=(?:"([^"]+)"|([^;\s]+))/i.exec(contentType || '');
    const b = m ? (m[1] || m[2]).trim() : '';
    const parts = b ? text.split('--' + b) : [text];
    const out = [];
    for (let i = 0; i < parts.length; i++) {
      const s = parts[i].indexOf('{');
      const e = parts[i].lastIndexOf('}');
      if (s < 0 || e <= s) continue;
      try { out.push(JSON.parse(parts[i].slice(s, e + 1))); } catch (err) {}
    }
    return out;
  }

  // Sob @defer os rates vivem só em incremental[].data; o 1o chunk traz o cart.
  function groupsFrom(chunks) {
    for (const chunk of chunks) {
      for (const step of chunk.incremental || []) {
        if (step && step.data && step.data.deliveryGroups) return step.data.deliveryGroups;
      }
    }
    return null;
  }

  // Pesca a contagem de dias no texto do carrier ("3 a 5 dias úteis", "1 dia
  // útil"). Sem match, devolve nulo — inventar prazo é proibido.
  function daysFrom(txt) {
    const s = String(txt || '');
    const range = RANGE_RE.exec(s);
    if (range) return [range[1], range[2]];
    const one = SINGLE_RE.exec(s);
    return one ? [one[1], one[1]] : [null, null];
  }

  // Shape idêntico ao de /cart/shipping_rates.json: quem exibe não sabe a fonte.
  // `amount` passa inteiro — arredondar quebraria a paridade com o checkout.
  function toRates(groups) {
    const opts = ((groups && groups.nodes) || []).flatMap((n) => (n && n.deliveryOptions) || []);
    return opts.map((o) => {
      // Ordem de autoridade: o prazo DECLARADO pelo carrier vale mais que o que
      // ele por acaso escreveu no título. Preencher `delivery_days_*` daqui é o
      // que devolve visão à lógica de dedupe/ordenação de quem exibe.
      let d = daysFrom(o.deliveryPromise);
      if (d[0] === null) d = daysFrom((o.title || '') + ' ' + (o.description || ''));
      return {
        name: o.title || o.handle || '',
        price: String((o.estimatedCost && o.estimatedCost.amount) || '0'),
        delivery_days_min: d[0],
        delivery_days_max: d[1],
      };
    });
  }

  function remember(cart, cep) {
    const lines = (cart.lines && cart.lines.nodes) || [];
    ssSet(CART_KEY, { id: cart.id, lineId: lines[0] && lines[0].id, cep: cep });
  }

  // Classifica userError do cartCreate POR ORIGEM, não em bloco. O `field` que
  // aponta para o endereço (CEP fora de faixa, UF recusada) é veredito sobre o
  // ENDEREÇO: 'coverage', o mesmo que o 422 do endpoint AJAX. Qualquer outro —
  // e o caso real é merchandise, produto ainda não publicado no canal do token
  // Headless (autoPublish=false) — é problema NOSSO, e acusar o CEP do cliente
  // por isso seria mentira: vira 'network', cuja copy oferece "tentar de novo".
  function cartUserError(errs) {
    const addr = errs.find((e) => ADDRESS_FIELD_RE.test(((e && e.field) || []).join('.')));
    const e = addr || errs[0] || {};
    return kindError(e.message || 'cart recusado', addr ? 'coverage' : 'network');
  }

  function cartInput(cfg) {
    const address = { countryCode: cfg.country || 'BR', zip: cfg.cep };
    // A Frenet cota por UF; mandar sempre custa menos que apostar no COUNTRY_CODE_ONLY.
    if (cfg.province) address.provinceCode = cfg.province;
    return {
      lines: [{ merchandiseId: gid(cfg.variantId), quantity: 1 }],
      // `_bl_ct_dt` = passe do app Blockify (Fraud Filter) para carrinho headless.
      // Este cart é efêmero e nunca chega ao checkout; sem o atributo a Function
      // do app troca o item por variante inválida e a API responde
      // MERCHANDISE_OUT_OF_STOCK (.ai/research/20261013-frete-pdp-storefront-esgotado.md).
      attributes: [
        { key: CART_ATTR, value: '1' },
        { key: '_bl_ct_dt', value: 'true' },
      ],
      // `deliveryAddress` = chave oneOf; NUNCA deliveryAddressPreferences (deprecado).
      delivery: {
        addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: address } }],
      },
    };
  }

  // Reusa o cart da sessão só com o MESMO CEP (endereço já gravado): troca a linha.
  async function ensureCart(cfg) {
    const saved = ssGet(CART_KEY);
    if (saved && saved.id && saved.lineId && saved.cep === cfg.cep) {
      const res = await gql(cfg, MUT_LINES, {
        id: saved.id,
        lines: [{ id: saved.lineId, merchandiseId: gid(cfg.variantId), quantity: 1 }],
      });
      // Cart velho/expirado não é falha: sem cart de volta, cai no cartCreate.
      const out = ((await res.json()).data || {}).cartLinesUpdate;
      if (out && out.cart) {
        remember(out.cart, cfg.cep);
        return out.cart.id;
      }
    }
    const res = await gql(cfg, MUT_CREATE, { input: cartInput(cfg) });
    const made = ((await res.json()).data || {}).cartCreate || {};
    const errs = made.userErrors || [];
    // Sem userErrors e sem cart, a culpa é da loja — 'network' (DÚVIDA-2 da
    // revisão da TASK-052).
    if (errs.length) throw cartUserError(errs);
    if (!made.cart) throw netError('sem cart');
    remember(made.cart, cfg.cep);
    return made.cart.id;
  }

  async function groupsAt(cfg, cartId, endpoint, promise) {
    const res = await gql({ ...cfg, endpoint: endpoint }, query(promise), { id: cartId });
    const text = await res.text();
    // Frenet > 10 s: a Shopify responde `Timed out`. Nulo = tentar de novo.
    return /timed out/i.test(text) ? null : groupsFrom(jsonChunks(text, res.headers.get('content-type')));
  }

  // FALLBACK OBRIGATÓRIO. Tenta a unstable com o prazo; se ela não devolver os
  // grupos — campo fora do schema, GraphQL error, rede — repete na versão
  // estável sem o campo. O pior caso é a cotação voltar sem prazo, exatamente o
  // comportamento anterior a esta correção; erro na cara do cliente, nunca.
  async function fetchRates(cfg, cartId) {
    const alt = VERSION_RE.test(cfg.endpoint)
      ? cfg.endpoint.replace(VERSION_RE, '/api/unstable/graphql.json')
      : ''; // endpoint fora do formato esperado: sem palpite, vai direto à estável
    // Grupos AUSENTES (nulo) = sem chunk incremental ou `Timed out`. Lista de
    // opções vazia, essa sim, é veredito do carrier e não vira nova tentativa.
    // A unstable vai até 2 vezes (Correção 1, TASK-176): no timeout a Shopify
    // já guardou as rates do cart e a 2a volta em ~4 s, com o prazo.
    for (let i = 0; alt && i < 2; i++) {
      try {
        const g = await groupsAt(cfg, cartId, alt, true);
        if (g) return toRates(g);
      } catch (e) {
        if (cfg.signal && cfg.signal.aborted) throw e;
      }
    }
    return toRates(await groupsAt(cfg, cartId, cfg.endpoint, false));
  }

  // cfg: { endpoint, token, variantId, cep, country?, province?, signal? }
  function quoteVariant(cfg) {
    if (!cfg || !cfg.endpoint || !cfg.token || !cfg.variantId || !cfg.cep) {
      return Promise.reject(netError('config incompleta'));
    }
    const key = gid(cfg.variantId) + ':' + cfg.cep;
    const cached = cacheGet(key);
    if (cached) return Promise.resolve(cached);
    // Dedupe: clique repetido e recotação dividem a mesma requisição.
    if (inflight.has(key)) return inflight.get(key);

    const run = ensureCart(cfg)
      .then((cartId) => fetchRates(cfg, cartId))
      .then((rates) => {
        if (rates.length) cacheSet(key, rates);
        return rates;
      })
      .finally(() => inflight.delete(key));

    inflight.set(key, run);
    return run;
  }

  // Combined listing troca o produto na mesma URL: o cache deixa de valer.
  function clearCache() {
    try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {}
  }

  // Superfície pública ÚNICA. Publicada na execução do script (antes de
  // qualquer DOMContentLoaded), então o inline — `defer` posterior — a encontra
  // pronta sem depender de evento.
  window.Shopinfo = window.Shopinfo || {};
  window.Shopinfo.shippingRates = {
    quote: quoteVariant,
    clearCache: clearCache,
    // Lê o cache sem rede: o teto da auto-cotação conta só chamada real.
    cached: (v, cep) => cacheGet(gid(v) + ':' + cep),
  };
})();
